/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import { sigintCreateSensor } from "../funcs/sigintCreateSensor.js";
import { sigintCreateSignal } from "../funcs/sigintCreateSignal.js";
import { sigintDeleteSensor } from "../funcs/sigintDeleteSensor.js";
import { sigintDeleteSignal } from "../funcs/sigintDeleteSignal.js";
import { sigintGetSensor } from "../funcs/sigintGetSensor.js";
import { sigintGetSignal } from "../funcs/sigintGetSignal.js";
import { sigintListSensors } from "../funcs/sigintListSensors.js";
import { sigintListSignals } from "../funcs/sigintListSignals.js";
import { sigintUpdateSensor } from "../funcs/sigintUpdateSensor.js";
import { sigintUpdateSignal } from "../funcs/sigintUpdateSignal.js";
import { ClientSDK, RequestOptions } from "../lib/sdks.js";
import { SigintSensor } from "../models/components/sigintsensor.js";
import { SigintSignal } from "../models/components/sigintsignal.js";
import {
  CreateSigintSensorRequest,
  CreateSigintSensorSecurity,
} from "../models/operations/createsigintsensor.js";
import {
  CreateSigintSignalRequest,
  CreateSigintSignalSecurity,
} from "../models/operations/createsigintsignal.js";
import {
  DeleteSigintSensorRequest,
  DeleteSigintSensorSecurity,
} from "../models/operations/deletesigintsensor.js";
import {
  DeleteSigintSignalRequest,
  DeleteSigintSignalSecurity,
} from "../models/operations/deletesigintsignal.js";
import {
  GetSigintSensorRequest,
  GetSigintSensorSecurity,
} from "../models/operations/getsigintsensor.js";
import {
  GetSigintSignalRequest,
  GetSigintSignalSecurity,
} from "../models/operations/getsigintsignal.js";
import {
  ListSigintSensorsRequest,
  ListSigintSensorsResponse,
  ListSigintSensorsSecurity,
} from "../models/operations/listsigintsensors.js";
import {
  ListSigintSignalsRequest,
  ListSigintSignalsResponse,
  ListSigintSignalsSecurity,
} from "../models/operations/listsigintsignals.js";
import {
  UpdateSigintSensorRequest,
  UpdateSigintSensorSecurity,
} from "../models/operations/updatesigintsensor.js";
import {
  UpdateSigintSignalRequest,
  UpdateSigintSignalSecurity,
} from "../models/operations/updatesigintsignal.js";
import { unwrapAsync } from "../types/fp.js";
import { PageIterator, unwrapResultIterator } from "../types/operations.js";

export class Sigint extends ClientSDK {
  /**
   * createSensor sigint
   *
   * @remarks
   * Create a sensor. multi_label permits independently applicable labels, exclusive makes attached signals compete and permits at most 255, and ordered_score uses ordered levels and permits at most 10. Empty and one-signal sensors are valid drafts. Omitted signal_ids creates an empty sensor; [] is explicitly empty. Repeated, deleted, missing, or cross-project signal IDs are rejected atomically.
   */
  async createSensor(
    request: CreateSigintSensorRequest,
    security?: CreateSigintSensorSecurity | undefined,
    options?: RequestOptions,
  ): Promise<SigintSensor> {
    return unwrapAsync(sigintCreateSensor(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * createSignal sigint
   *
   * @remarks
   * Create a reusable custom signal. Names are trimmed and must contain 1 to 200 characters. Description and classifier criteria are optional authoring metadata; an empty string clears either field.
   */
  async createSignal(
    request: CreateSigintSignalRequest,
    security?: CreateSigintSignalSecurity | undefined,
    options?: RequestOptions,
  ): Promise<SigintSignal> {
    return unwrapAsync(sigintCreateSignal(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * deleteSensor sigint
   *
   * @remarks
   * Soft-delete a sensor and its memberships. Shared custom signals remain active.
   */
  async deleteSensor(
    request: DeleteSigintSensorRequest,
    security?: DeleteSigintSensorSecurity | undefined,
    options?: RequestOptions,
  ): Promise<SigintSensor> {
    return unwrapAsync(sigintDeleteSensor(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * deleteSignal sigint
   *
   * @remarks
   * Soft-delete a custom signal and detach it from every sensor in the selected project. Surviving sensor signal order is compacted; sensors may remain incomplete drafts.
   */
  async deleteSignal(
    request: DeleteSigintSignalRequest,
    security?: DeleteSigintSignalSecurity | undefined,
    options?: RequestOptions,
  ): Promise<SigintSignal> {
    return unwrapAsync(sigintDeleteSignal(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * getSensor sigint
   *
   * @remarks
   * Get an active sensor with its ordered signal_ids. The collection is always returned and is [] when no signals are attached.
   */
  async getSensor(
    request: GetSigintSensorRequest,
    security?: GetSigintSensorSecurity | undefined,
    options?: RequestOptions,
  ): Promise<SigintSensor> {
    return unwrapAsync(sigintGetSensor(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * getSignal sigint
   *
   * @remarks
   * Get an active custom signal by ID in the selected project.
   */
  async getSignal(
    request: GetSigintSignalRequest,
    security?: GetSigintSignalSecurity | undefined,
    options?: RequestOptions,
  ): Promise<SigintSignal> {
    return unwrapAsync(sigintGetSignal(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * listSensors sigint
   *
   * @remarks
   * List active sensors in deterministic ID order with each sensor's ordered signal_ids from one consistent database snapshot. The collection is [] for an unattached sensor.
   */
  async listSensors(
    request?: ListSigintSensorsRequest | undefined,
    security?: ListSigintSensorsSecurity | undefined,
    options?: RequestOptions,
  ): Promise<PageIterator<ListSigintSensorsResponse, { cursor: string }>> {
    return unwrapResultIterator(sigintListSensors(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * listSignals sigint
   *
   * @remarks
   * List active custom signals in deterministic ID order. The cursor is the final ID from the previous page.
   */
  async listSignals(
    request?: ListSigintSignalsRequest | undefined,
    security?: ListSigintSignalsSecurity | undefined,
    options?: RequestOptions,
  ): Promise<PageIterator<ListSigintSignalsResponse, { cursor: string }>> {
    return unwrapResultIterator(sigintListSignals(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * updateSensor sigint
   *
   * @remarks
   * Partially update a sensor. Omitted fields and JSON null preserve stored values. Empty optional text clears it. Omitted signal_ids preserves membership, while [] clears it and a non-empty array replaces and reorders it atomically. Mode changes validate the final membership: exclusive permits at most 255 signals and ordered_score at most 10; incomplete drafts remain valid.
   */
  async updateSensor(
    request: UpdateSigintSensorRequest,
    security?: UpdateSigintSensorSecurity | undefined,
    options?: RequestOptions,
  ): Promise<SigintSensor> {
    return unwrapAsync(sigintUpdateSensor(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * updateSignal sigint
   *
   * @remarks
   * Partially update a custom signal. Omitted fields and JSON null leave existing values unchanged. Supplying an empty optional-text value clears it. A supplied name is trimmed and must contain 1 to 200 characters.
   */
  async updateSignal(
    request: UpdateSigintSignalRequest,
    security?: UpdateSigintSignalSecurity | undefined,
    options?: RequestOptions,
  ): Promise<SigintSignal> {
    return unwrapAsync(sigintUpdateSignal(
      this,
      request,
      security,
      options,
    ));
  }
}
